/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、lint 警告の前提 (= subtree 破棄)
 * に該当しない。
 */
import { useTranslation } from 'react-i18next'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { planCategoryLabel } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/planCategory'
import type { GenerationSalesMasterRow } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/types'
import { ExpiredBadge } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredBadge'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  rows: GenerationSalesMasterRow[]
  isLoading: boolean
  onEdit: (row: GenerationSalesMasterRow) => void
  onDelete: (row: GenerationSalesMasterRow) => void
  today: CivilDate
  /** 空 state のメッセージ上書き (AC-09)。 */
  emptyLabel?: string
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

export const GenerationSalesPlanMasterTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  today,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columns: Array<MasterDataTableColumn<GenerationSalesMasterRow>> = [
    // [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // design (`design/admin/occto.html` 発電販売計画タブ) は 8 列目 (備考 と 適用開始日 の間。
    // 静的 thead には無く _occtoSyncStatusColumn が挿入する) だが、
    // 行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
    {
      key: 'status',
      header: t('page:adminOcctoMaster.common.column.status'),
      cell: (row) => (
        <ExpiredBadge
          today={today}
          validFrom={row.validFrom}
          validTo={row.validTo}
        />
      ),
    },
    {
      key: 'submitterCode',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.submitterCode',
      ),
      cell: (row) => row.submitterCode,
    },
    {
      // AC-09 (occto-master-audit-fix-bundle.md): 紐づく計画提出者のエリアを表示
      key: 'areaCode',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.areaCode',
      ),
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      // generation-sales-plan-category 設計書: 計画区分 (通常電源 / FIT 特例電源)
      key: 'planCategory',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.planCategory',
      ),
      cell: (row) => planCategoryLabel(row.planCategory),
    },
    {
      key: 'codeCount',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.codeCount',
      ),
      format: 'number',
      cell: (row) => row.codeCount.toLocaleString(),
    },
    {
      key: 'bgCount',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.bgCount',
      ),
      format: 'number',
      cell: (row) => row.bgCount.toLocaleString(),
    },
    {
      key: 'plantCount',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.plantCount',
      ),
      format: 'number',
      cell: (row) => row.plantCount.toLocaleString(),
    },
    {
      key: 'note',
      header: t('page:adminOcctoMaster.generationSalesPlanMaster.column.note'),
      cell: (row) => row.note ?? '',
    },
    {
      key: 'validFrom',
      header: t('page:adminOcctoMaster.common.column.validFrom'),
      cell: (row) => formatCivilDate(row.validFrom),
    },
    {
      key: 'validTo',
      header: t('page:adminOcctoMaster.common.column.validTo'),
      cell: (row) =>
        row.validTo !== undefined ? formatCivilDate(row.validTo) : '—',
    },
    {
      key: 'updatedAt',
      header: t(
        'page:adminOcctoMaster.generationSalesPlanMaster.column.updatedAt',
      ),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
